import { format, isValid } from "date-fns";
import { useEffect, useId, useMemo, useState } from "react";
import { Dropdown } from "../Dropdown";
import type {
  IDatePickerContext as IDatePickerContextType,
  IDatePickerProps,
} from "./types";
import { DatePickerProvider } from "./context";
import { DatePickerTrigger } from "./DatePickerTrigger";
import { DatePickerContent } from "./DatePickerContent";

const DatePicker = (props: IDatePickerProps) => {
  const [open, setOpen] = useState(false);

  const formatValue = (date: Date | undefined): string => {
    if (!date || !isValid(date)) {
      return "";
    }
    return format(date, "MM/dd/yyyy");
  };

  const [displayValue, setDisplayValue] = useState<string>(
    formatValue(props.value),
  );

  useEffect(() => {
    if (props.value !== undefined) {
      setDisplayValue(formatValue(props.value));
    }
  }, [props.value]);

  const generatedId = useId();
  const triggerId = props.id || `${generatedId}-trigger`;
  const contentId = props.id ? `${props.id}-content` : `${generatedId}-content`;

  const context = useMemo((): IDatePickerContextType => {
    return {
      open,
      setOpen,
      displayValue,
      setDisplayValue,
      triggerId,
      contentId,
      ...props,
    };
  }, [open, displayValue, triggerId, contentId, props]);

  return (
    <DatePickerProvider value={context}>
      <Dropdown
        trigger={<DatePickerTrigger />}
        content={<DatePickerContent />}
        triggerId={triggerId}
        contentId={contentId}
        open={open}
        onOpenChange={setOpen}
        disabled={props.disabled}
        interactions={["click", "dismiss"]}
        role="dialog"
        floatingFocusManager={{
          initialFocus: -1,
          modal: false,
          returnFocus: true,
          restoreFocus: false,
        }}
        placement="bottom-start"
        offset={8}
      />
    </DatePickerProvider>
  );
};

export { DatePicker };
